import React, { useState } from 'react';
import { CheckCircle2, FileSpreadsheet, Upload, Zap } from 'lucide-react';
import { PLAGES_PAR_FORMULE } from '../../pages/turpe/constants';
import ImportRecapPanel from './ImportRecapPanel';

/**
 * Étape 4 TURPE — extrait de CreateAnalyseTURPE.jsx pour réduire la taille
 * du composant parent. Reçoit toutes les dépendances via un objet `ctx` unique.
 */
export const Step4Donnees = ({ ctx }) => {
  const {
    analyseType,
    formData,
    handleImportDonneesBrutes,
    handleImportFichierMultiPDL,
    handleImportMultipleFichiers,
    downloadTemplatePuissances,
    handleImportPuissancesSouscrites,
    handleSavePuissanceFacture,
    importMultiPDLProgress,
    importRecap,
    setImportRecap,
    puissancesFacturesParPDL,
    puissancesMaxParPDL,
    sauvegarderBrouillon,
    setFormData,
    setStep,
    step,
  } = ctx;

  // États drag & drop pour les deux zones d'import multi
  const [dragOverMulti, setDragOverMulti] = useState(false);
  const [dragOverIndiv, setDragOverIndiv] = useState(false);

  // Wrappers : transforment un drop event en pseudo-event compatible avec les handlers existants
  const handleDropMultiPDL = (e) => {
    e.preventDefault();
    setDragOverMulti(false);
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleImportFichierMultiPDL({ target: { files, value: '' } });
    }
  };

  const handleDropIndividuels = (e) => {
    e.preventDefault();
    setDragOverIndiv(false);
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleImportMultipleFichiers({ target: { files, value: '' } });
    }
  };

  return (
        <div className="bg-white border border-[#BFC6CC] rounded-lg p-8">
          <h2 className="text-2xl font-heading font-semibold text-[#191D1E] mb-6">
            Données TURPE et Puissances
          </h2>
          
          <p className="text-[#687175] mb-6">
            {formData.pdls.length} PDL configuré(s)
          </p>

          {/* Mono : Saisie manuelle */}
          {analyseType === 'mono' && formData.pdls.map((pdl, idx) => {
            const plages = PLAGES_PAR_FORMULE[pdl.formule_actuelle] || [];
            
            // Fonction pour mettre à jour les puissances actuelles
            const updatePuissanceActuelle = (plage, value) => {
              const newPdls = [...formData.pdls];
              newPdls[idx] = {
                ...newPdls[idx],
                puissances_actuelles: {
                  ...newPdls[idx].puissances_actuelles,
                  [plage]: parseFloat(value) || 0
                }
              };
              setFormData({...formData, pdls: newPdls});
            };
            
            return (
              <div key={idx} className="space-y-6">
                <div className="p-4 bg-[#F5F6F9] rounded-lg">
                  <div className="font-mono font-semibold text-[#191D1E]">{pdl.pdl}</div>
                  <div className="text-sm text-[#687175]">
                    {pdl.tension} • {pdl.formule_actuelle} • {pdl.contrat}
                  </div>
                </div>

                <div>
                  <h3 className="font-semibold text-[#191D1E] mb-4">Puissances Souscrites Actuelles (kVA)</h3>
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    {plages.map((plage) => (
                      <div key={plage}>
                        <label className="block text-sm text-[#687175] mb-1">{plage}</label>
                        <input
                          type="number"
                          placeholder="0"
                          value={pdl.puissances_actuelles?.[plage] || ''}
                          onChange={(e) => updatePuissanceActuelle(plage, e.target.value)}
                          className="w-full px-3 py-2 border border-[#BFC6CC] rounded-lg focus:ring-2 focus:ring-[#397DFF] font-mono text-sm"
                        />
                      </div>
                    ))}
                  </div>
                  {/* Puissance maximale globale souscrite (pour le graphique) */}
                  <div className="p-4 bg-[#FEF3C7] border border-[#F9C80E] rounded-lg">
                    <label className="block text-sm font-medium text-[#191D1E] mb-2">
                      Puissance Souscrite Maximale (pour comparaison graphique)
                    </label>
                    <div className="flex items-center gap-3">
                      <input
                        type="number"
                        placeholder="Ex: 250"
                        value={pdl.puissances_actuelles?.max || ''}
                        onChange={(e) => updatePuissanceActuelle('max', e.target.value)}
                        className="w-40 px-3 py-2 border border-[#BFC6CC] rounded-lg focus:ring-2 focus:ring-[#397DFF] font-mono text-sm"
                      />
                      <span className="text-sm text-[#687175]">kVA</span>
                      <span className="text-xs text-[#687175]">→ Affichée en ligne rouge pointillée sur la courbe de charge</span>
                    </div>
                  </div>
                </div>

                {/* Import données brutes mono */}
                <div className="p-4 bg-[#E8F4FD] border border-[#397DFF] rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium text-[#191D1E]">
                        Import Données Brutes (12 mois)
                      </div>
                      <div className="text-xs text-[#687175] mt-1">
                        Calcul automatique puissances maximales (kVA)
                      </div>
                    </div>
                    <label className="flex items-center gap-2 px-4 py-2 bg-[#397DFF] hover:bg-[#4672B6] text-white rounded-lg cursor-pointer text-sm">
                      <Upload className="w-4 h-4" />
                      Charger CSV
                      <input
                        type="file"
                        accept=".csv,.txt"
                        onChange={(e) => handleImportDonneesBrutes(pdl.pdl, e)}
                        className="hidden"
                      />
                    </label>
                  </div>
                </div>

                {/* Résultats import */}
                {puissancesMaxParPDL[pdl.pdl] && (
                  <div className="border border-[#10D183] rounded-lg overflow-hidden">
                    <div className="bg-[#10D183]/10 px-4 py-2 border-b border-[#10D183]">
                      <div className="text-sm font-medium text-[#191D1E]">
                        ✓ Puissances Maximales Calculées
                      </div>
                    </div>
                    <div className="p-4">
                      <table className="w-full text-sm">
                        <thead className="bg-[#F5F6F9]">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs text-[#687175]">Mois</th>
                            <th className="px-3 py-2 text-right text-xs text-[#687175]">Pmax (kVA)</th>
                            <th className="px-3 py-2 text-center text-xs text-[#687175]">P. Facture (kVA)</th>
                            <th className="px-3 py-2 text-right text-xs text-[#687175]">Points</th>
                            <th className="px-3 py-2 text-center text-xs text-[#687175]">Qualité</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-[#BFC6CC]">
                          {puissancesMaxParPDL[pdl.pdl].puissancesMaxParMois?.map((mois, idx) => (
                            <tr key={idx} className="hover:bg-[#F5F6F9]">
                              <td className="px-3 py-2 text-[#191D1E]">{mois.mois}</td>
                              <td className="px-3 py-2 text-right font-mono font-semibold text-[#397DFF]">
                                {mois.pmax_kVA.toFixed(1)}
                              </td>
                              <td className="px-3 py-2 text-center">
                                <input
                                  type="number"
                                  min="0"
                                  step="0.1"
                                  placeholder="—"
                                  value={puissancesFacturesParPDL[pdl.pdl]?.[mois.mois] || ''}
                                  onChange={(e) => handleSavePuissanceFacture(pdl.pdl, mois.mois, e.target.value)}
                                  className="w-20 px-2 py-1 border border-[#BFC6CC] rounded text-center font-mono text-sm focus:ring-2 focus:ring-amber-400 focus:border-amber-400"
                                  data-testid={`puissance-facture-${pdl.pdl}-${idx}`}
                                />
                              </td>
                              <td className="px-3 py-2 text-right text-[#687175]">
                                {mois.nb_points.toLocaleString('fr-FR')}
                              </td>
                              <td className="px-3 py-2 text-center">
                                <span className={`px-2 py-1 rounded-full text-xs ${
                                  mois.qualite === 'Bon' ? 'bg-[#10D183]/20 text-[#10D183]' :
                                  mois.qualite === 'Moyen' ? 'bg-[#F9C80E]/20 text-[#F9C80E]' :
                                  'bg-red-100 text-red-600'
                                }`}>
                                  {mois.qualite} ({mois.taux_complet.toFixed(0)}%)
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <p className="text-xs text-[#687175] mt-2 italic">
                        Renseignez les puissances maximales affichées sur vos factures (optionnel). Elles seront comparées aux puissances mesurées sur le graphique.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {/* Multi : Import centralisé + Puissances souscrites */}
          {analyseType === 'multi' && (
            <div className="space-y-6">
              {/* Zone d'import : fichier unique multi-PDL OU plusieurs fichiers individuels */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Option 1 : Fichier unique Enedis multi-PDL */}
                <div
                  onDragOver={(e) => { e.preventDefault(); if (importMultiPDLProgress === null) setDragOverMulti(true); }}
                  onDragLeave={() => setDragOverMulti(false)}
                  onDrop={importMultiPDLProgress === null ? handleDropMultiPDL : (e) => e.preventDefault()}
                  className={`p-6 border-2 border-dashed rounded-lg transition-colors ${
                    dragOverMulti
                      ? 'border-[#10D183] bg-[#10D183]/20 scale-[1.01] shadow-md'
                      : 'border-[#10D183] bg-[#10D183]/5'
                  }`}
                  data-testid="dropzone-multi-pdl"
                >
                  <div className="text-center">
                    <FileSpreadsheet className={`w-10 h-10 mx-auto mb-3 ${dragOverMulti ? 'text-[#0AB571] animate-bounce' : 'text-[#10D183]'}`} />
                    <h3 className="font-semibold text-[#191D1E] mb-2">Fichier Enedis Multi-PDL</h3>
                    <p className="text-sm text-[#687175] mb-4">
                      {dragOverMulti
                        ? 'Relâchez pour téléverser le fichier'
                        : <>Un seul fichier CSV contenant tous les PDL.<br/>Glissez-déposez ou cliquez ci-dessous.</>
                      }
                    </p>
                    {importMultiPDLProgress !== null ? (
                      <div className="max-w-xs mx-auto">
                        <div className="bg-gray-200 rounded-full h-3 mb-2 overflow-hidden">
                          <div className="bg-[#10D183] h-3 rounded-full transition-all duration-300" style={{ width: `${importMultiPDLProgress}%` }} />
                        </div>
                        <p className="text-xs text-[#687175]">Analyse en cours... {importMultiPDLProgress}%</p>
                      </div>
                    ) : (
                      <label className="cursor-pointer" data-testid="import-multi-pdl-enedis">
                        <span className="px-6 py-3 bg-[#10D183] text-white rounded-lg inline-flex items-center gap-2 hover:bg-[#0AB571] transition-colors">
                          <Upload className="w-4 h-4" />
                          Charger fichier multi-PDL
                        </span>
                        <input
                          type="file"
                          accept=".csv,.txt"
                          onChange={handleImportFichierMultiPDL}
                          className="hidden"
                        />
                      </label>
                    )}
                  </div>
                </div>

                {/* Option 2 : Plusieurs fichiers individuels */}
                <div
                  onDragOver={(e) => { e.preventDefault(); setDragOverIndiv(true); }}
                  onDragLeave={() => setDragOverIndiv(false)}
                  onDrop={handleDropIndividuels}
                  className={`p-6 border-2 border-dashed rounded-lg transition-colors ${
                    dragOverIndiv
                      ? 'border-[#397DFF] bg-[#397DFF]/20 scale-[1.01] shadow-md'
                      : 'border-[#397DFF] bg-[#E8F4FD]/30'
                  }`}
                  data-testid="dropzone-individuels"
                >
                  <div className="text-center">
                    <Upload className={`w-10 h-10 mx-auto mb-3 ${dragOverIndiv ? 'text-[#4672B6] animate-bounce' : 'text-[#397DFF]'}`} />
                    <h3 className="font-semibold text-[#191D1E] mb-2">Fichiers CSV Individuels</h3>
                    <p className="text-sm text-[#687175] mb-4">
                      {dragOverIndiv
                        ? 'Relâchez pour téléverser les fichiers'
                        : <>Un ou plusieurs fichiers (mono ou multi-PDL).<br/>Cumul avec les imports précédents · PDL créés automatiquement.</>
                      }
                    </p>
                    <label className="cursor-pointer">
                      <span className="px-6 py-3 bg-[#397DFF] text-white rounded-lg inline-flex items-center gap-2 hover:bg-[#4672B6] transition-colors">
                        <Upload className="w-4 h-4" />
                        Sélectionner les fichiers CSV
                      </span>
                      <input
                        type="file"
                        multiple
                        accept=".csv,.txt"
                        onChange={handleImportMultipleFichiers}
                        className="hidden"
                        data-testid="import-individuels-input"
                      />
                    </label>
                  </div>
                </div>
              </div>

              {/* Récapitulatif visuel du dernier import groupé */}
              <ImportRecapPanel recap={importRecap} onDismiss={() => setImportRecap(null)} />

              {/* Import groupé des puissances souscrites (modèle Excel) */}
              <div className="p-5 border-2 border-dashed border-amber-300 rounded-lg bg-amber-50/50" data-testid="bloc-import-puissances">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div>
                    <h3 className="font-semibold text-[#191D1E] flex items-center gap-2">
                      <Zap className="w-4 h-4 text-amber-600" />
                      Puissances souscrites — import par fichier
                    </h3>
                    <p className="text-sm text-[#687175] mt-1">
                      Téléchargez le modèle, renseignez la puissance souscrite (kVA) de chaque PDL par plage
                      (HPH, HCH, HPB, HCB, Pointe, ou Unique en BT), puis réimportez-le. Les PDL absents seront créés automatiquement.
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2 shrink-0">
                    <button
                      onClick={downloadTemplatePuissances}
                      className="px-4 py-2 border border-amber-400 text-amber-700 bg-white rounded-lg inline-flex items-center gap-2 hover:bg-amber-100 transition-colors text-sm"
                      data-testid="download-template-puissances"
                    >
                      <FileSpreadsheet className="w-4 h-4" />
                      Télécharger le modèle
                    </button>
                    <label className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg inline-flex items-center gap-2 cursor-pointer transition-colors text-sm" data-testid="import-puissances-label">
                      <Upload className="w-4 h-4" />
                      Importer les puissances
                      <input
                        type="file"
                        accept=".xlsx,.xls,.csv"
                        onChange={handleImportPuissancesSouscrites}
                        className="hidden"
                        data-testid="import-puissances-input"
                      />
                    </label>
                  </div>
                </div>
              </div>


              {/* État des imports par PDL */}
              <div className="space-y-4">
                <h3 className="font-semibold text-[#191D1E]">État des données par PDL</h3>
                
                {formData.pdls.map((pdl, idx) => {
                  const plagesFormule = PLAGES_PAR_FORMULE[pdl.formule_actuelle] || [];
                  const plagesImportees = Object.keys(pdl.puissances_actuelles || {});
                  // Fallback : si aucune formule n'est encore choisie (ex: PDL auto-créé à l'import),
                  // on affiche quand même les inputs (plages importées, sinon jeu par défaut selon la tension).
                  const plages = plagesFormule.length
                    ? plagesFormule
                    : (plagesImportees.length
                        ? plagesImportees
                        : (pdl.tension === 'HTA' ? ['Pointe', 'HPH', 'HCH', 'HPB', 'HCB'] : ['HPH', 'HCH', 'HPB', 'HCB']));
                  const hasData = puissancesMaxParPDL[pdl.pdl];
                  
                  // Fonction pour mettre à jour les puissances actuelles
                  const updatePuissanceActuelle = (plage, value) => {
                    const newPdls = [...formData.pdls];
                    newPdls[idx] = {
                      ...newPdls[idx],
                      puissances_actuelles: {
                        ...newPdls[idx].puissances_actuelles,
                        [plage]: parseFloat(value) || 0
                      }
                    };
                    setFormData({...formData, pdls: newPdls});
                  };
                  
                  return (
                    <div key={idx} className="border border-[#BFC6CC] rounded-lg overflow-hidden">
                      {/* En-tête PDL */}
                      <div className={`px-6 py-3 flex items-center justify-between ${hasData ? 'bg-[#10D183]' : 'bg-[#687175]'} text-white`}>
                        <div>
                          <div className="font-mono font-semibold">{pdl.pdl}</div>
                          <div className="text-sm opacity-90">{pdl.tension} • {pdl.formule_actuelle}</div>
                        </div>
                        <div className="flex items-center gap-3">
                          {hasData ? (
                            <div className="flex items-center gap-2 text-sm">
                              <CheckCircle2 className="w-5 h-5" />
                              <span>{puissancesMaxParPDL[pdl.pdl].tousLesPoints?.length?.toLocaleString('fr-FR')} points</span>
                            </div>
                          ) : (
                            <div className="text-sm opacity-80">En attente de données</div>
                          )}
                          {/* Bouton import individuel en fallback */}
                          <label className="flex items-center gap-2 px-3 py-1.5 bg-white/20 hover:bg-white/30 rounded cursor-pointer text-xs">
                            <Upload className="w-3 h-3" />
                            Import
                            <input
                              type="file"
                              accept=".csv,.txt"
                              onChange={(e) => handleImportDonneesBrutes(pdl.pdl, e)}
                              className="hidden"
                            />
                          </label>
                        </div>
                      </div>
                      
                      {/* Contenu : Puissances souscrites actuelles */}
                      <div className="p-4 space-y-4">
                        <div>
                          <h4 className="font-medium text-[#191D1E] mb-3 flex items-center gap-2">
                            <Zap className="w-4 h-4 text-[#397DFF]" />
                            Puissances Souscrites Actuelles ({pdl.tension === 'HTA' ? 'kW' : 'kVA'})
                          </h4>
                          <div className={`grid gap-3 ${plages.length === 5 ? 'grid-cols-5' : 'grid-cols-4'}`}>
                            {plages.map((plage) => (
                              <div key={plage}>
                                <label className="block text-xs text-[#687175] mb-1">{plage}</label>
                                <input
                                  type="number"
                                  min="0"
                                  step="1"
                                  value={pdl.puissances_actuelles?.[plage] || ''}
                                  onChange={(e) => updatePuissanceActuelle(plage, e.target.value)}
                                  placeholder="0"
                                  className="w-full px-3 py-2 border border-[#BFC6CC] rounded-lg font-mono text-sm text-center"
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                        
                        {/* Aperçu données brutes si chargées */}
                        {hasData && puissancesMaxParPDL[pdl.pdl].puissancesMaxParMois && (
                          <div className="pt-3 border-t border-[#BFC6CC]">
                            <div className="text-xs text-[#687175] mb-2">Puissances max observées par mois :</div>
                            <div className="flex flex-wrap gap-2">
                              {puissancesMaxParPDL[pdl.pdl].puissancesMaxParMois.slice(0, 12).map((mois, i) => (
                                <div key={i} className="text-center px-2 py-1 bg-[#F5F6F9] rounded text-xs">
                                  <div className="text-[#687175]">{mois.mois.substring(5)}</div>
                                  <div className="font-mono font-bold text-[#397DFF]">{mois.pmax_kVA.toFixed(0)}</div>
                                </div>
                              ))}
                            </div>
                            {/* Saisie puissances factures */}
                            <div className="mt-3 pt-3 border-t border-[#BFC6CC]">
                              <div className="text-xs text-[#687175] mb-2">Puissances max facturées (optionnel) :</div>
                              <div className="flex flex-wrap gap-2">
                                {puissancesMaxParPDL[pdl.pdl].puissancesMaxParMois.slice(0, 12).map((mois, i) => (
                                  <div key={i} className="text-center">
                                    <div className="text-[#687175] text-xs mb-1">{mois.mois.substring(5)}</div>
                                    <input
                                      type="number"
                                      min="0"
                                      step="0.1"
                                      placeholder="—"
                                      value={puissancesFacturesParPDL[pdl.pdl]?.[mois.mois] || ''}
                                      onChange={(e) => handleSavePuissanceFacture(pdl.pdl, mois.mois, e.target.value)}
                                      className="w-16 px-1 py-1 border border-amber-300 rounded text-center font-mono text-xs focus:ring-1 focus:ring-amber-400"
                                      data-testid={`puissance-facture-multi-${pdl.pdl}-${i}`}
                                    />
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Résumé global */}
              <div className={`p-4 rounded-lg border ${
                Object.keys(puissancesMaxParPDL).length === formData.pdls.length 
                  ? 'bg-[#10D183]/10 border-[#10D183]' 
                  : 'bg-[#E8F4FD] border-[#397DFF]'
              }`}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium text-[#191D1E]">
                      {Object.keys(puissancesMaxParPDL).length} / {formData.pdls.length} PDL avec données brutes
                    </div>
                    <div className="text-sm text-[#687175]">
                      {Object.keys(puissancesMaxParPDL).length === formData.pdls.length 
                        ? '✓ Tous les PDL ont leurs données. N\'oubliez pas de saisir les puissances souscrites actuelles.'
                        : 'Importez les fichiers CSV manquants ou utilisez l\'import individuel.'}
                    </div>
                  </div>
                  {Object.keys(puissancesMaxParPDL).length === formData.pdls.length && (
                    <CheckCircle2 className="w-8 h-8 text-[#10D183]" />
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-between gap-3 pt-6">
            <button onClick={() => setStep(3)} className="px-6 py-3 border border-[#BFC6CC] text-[#687175] rounded-lg">← Retour</button>
            <div className="flex gap-3">
              <button onClick={sauvegarderBrouillon} className="px-4 py-2 border border-[#F9C80E] text-[#F9C80E] rounded-lg">💾 Brouillon</button>
              <button onClick={() => setStep(5)} className="bg-[#397DFF] text-white py-3 px-8 rounded-lg">Suivant →</button>
            </div>
          </div>
        </div>
  );
};

export default Step4Donnees;
